import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { gapi, errMsg, fmtDateTime, fmtEuro } from "../api";
import { useGAuth } from "../GAuth";
import { TIMELINE_TIPI, STATI_COMMERCIALI } from "../labels";
import { PageHeader, Panel, Btn, Badge, inputCls } from "../components/ui";
import Timeline from "../components/Timeline";
import ContattoForm from "./ContattoForm";
import { DocChecklist } from "../components/Documenti";

const NEW = { tipoSoggetto: "persona", nome: "", cognome: "", ruoli: ["da_qualificare"], telefoni: [{ numero: "", etichetta: "cellulare" }], emails: [], tag: [], consensi: [], priorita: "media", stato: "nuovo" };

function MergeDialog({ keepId, dup, onClose, onDone }) {
  const [prev, setPrev] = useState(null);
  useEffect(() => { if (dup) gapi.get("/contacts/merge/preview", { params: { keepId, mergeId: dup.id } }).then((r) => setPrev(r.data)).catch((e) => toast.error(errMsg(e))); }, [dup, keepId]);
  const run = async () => { try { await gapi.post("/contacts/merge", { keepId, mergeId: dup.id }); toast.success("Contatti uniti"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const r = prev?.risultato;
  return (
    <Dialog open={!!dup} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="merge-dialog">
        <DialogHeader><DialogTitle>Unisci «{dup?.displayName}» in questa scheda</DialogTitle><DialogDescription>Anteprima del risultato. La scheda duplicata verrà archiviata e collegata a questa.</DialogDescription></DialogHeader>
        {!prev ? <p className="text-sm">Caricamento…</p> : (
          <div className="text-sm space-y-2" data-testid="merge-preview">
            <p><b>Nome:</b> {r.displayName || `${r.nome} ${r.cognome}`}</p>
            <p><b>Telefoni:</b> {r.telefoni.map((t) => t.numero).join(", ") || "—"}</p>
            <p><b>Email:</b> {r.emails.map((e) => e.indirizzo).join(", ") || "—"}</p>
            <p><b>Ruoli:</b> {r.ruoli.join(", ")}</p>
            <p className="text-xs text-anthracite/55">Verranno spostati: {Object.entries(prev.collegamenti).map(([k, v]) => `${v} ${k}`).join(" · ")}</p>
          </div>
        )}
        <Btn onClick={run} disabled={!prev} data-testid="merge-confirm">Conferma unione</Btn>
      </DialogContent>
    </Dialog>
  );
}

function RegistraContatto({ cid, onDone }) {
  const [f, setF] = useState({ tipo: "chiamata", testo: "", esito: "", prossimoRichiamo: "" });
  const submit = async (e) => {
    e.preventDefault();
    try { await gapi.post(`/contacts/${cid}/timeline`, { ...f, prossimoRichiamo: f.prossimoRichiamo || null }); setF({ tipo: "chiamata", testo: "", esito: "", prossimoRichiamo: "" }); toast.success("Registrato"); onDone(); }
    catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <form onSubmit={submit} className="space-y-2" data-testid="timeline-form">
      <select className={inputCls} value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} data-testid="timeline-type">{Object.entries(TIMELINE_TIPI).filter(([k]) => k !== "visita").map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
      <textarea required rows={3} className={`${inputCls} h-auto py-2`} placeholder="Cosa è stato detto o fatto" value={f.testo} onChange={(e) => setF({ ...f, testo: e.target.value })} data-testid="timeline-text" />
      <input className={inputCls} placeholder="Esito (facoltativo)" value={f.esito} onChange={(e) => setF({ ...f, esito: e.target.value })} />
      <label className="block text-xs text-anthracite/55">Prossimo richiamo<input type="datetime-local" className={inputCls} value={f.prossimoRichiamo} onChange={(e) => setF({ ...f, prossimoRichiamo: e.target.value })} /></label>
      <Btn type="submit" className="w-full" data-testid="timeline-submit">Registra</Btn>
    </form>
  );
}

function Collegati({ c }) {
  return (
    <>
      <Panel title="Richieste di ricerca" actions={<Link to={`/gestionale/richieste/nuova?contactId=${c.id}`} className="text-xs font-semibold text-brand inline-flex items-center gap-1" data-testid="contact-new-request"><Plus className="h-3.5 w-3.5" /> Nuova</Link>}>
        {!c.richieste?.length ? <p className="text-sm text-anthracite/45">Nessuna richiesta.</p> : c.richieste.map((r) => (
          <Link key={r.id} to={`/gestionale/richieste/${r.id}`} className="block text-sm py-1.5 hover:underline">{r.tipo === "affitto" ? "Affitto" : "Acquisto"} · {(r.comuni || []).join(", ") || "qualsiasi comune"} · fino a {fmtEuro(r.budgetMax)} <Badge>{r.stato}</Badge></Link>
        ))}
      </Panel>
      <Panel title="Immobili di proprietà">
        {!c.immobili?.length ? <p className="text-sm text-anthracite/45">Nessun immobile collegato.</p> : c.immobili.map((p) => (
          <Link key={p.id} to={`/gestionale/immobili/${p.id}`} className="block text-sm py-1.5 hover:underline">{p.codiceRif} · {p.titolo} <Badge>{STATI_COMMERCIALI[p.statoCommerciale]}</Badge></Link>
        ))}
      </Panel>
      <Panel title="Attività">
        {!c.attivita?.length ? <p className="text-sm text-anthracite/45">Nessuna attività.</p> : c.attivita.map((t) => (
          <p key={t.id} className="text-sm py-1"><Badge tone={t.stato === "completata" ? "green" : "blue"}>{t.stato.replace("_", " ")}</Badge> {t.titolo} · {fmtDateTime(t.scadenza)}</p>
        ))}
      </Panel>
    </>
  );
}

export default function ContattoDetail() {
  const { id } = useParams();
  const isNew = id === "nuovo";
  const navigate = useNavigate();
  const { can, user } = useGAuth();
  const [c, setC] = useState(isNew ? { ...NEW, responsabileId: user.id } : null);
  const [timeline, setTimeline] = useState([]);
  const [users, setUsers] = useState([]);
  const [dups, setDups] = useState(null);
  const [mergeDup, setMergeDup] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (isNew) return;
    gapi.get(`/contacts/${id}`).then((r) => setC(r.data)).catch((e) => toast.error(errMsg(e)));
    gapi.get(`/contacts/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
  }, [id, isNew]);
  useEffect(() => { load(); gapi.get("/users/options").then((r) => setUsers(r.data)).catch(() => {}); }, [load]);

  const save = async (ignora = false) => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await gapi.post("/contacts", { ...c, ignoraDuplicati: ignora });
        toast.success("Contatto creato"); navigate(`/gestionale/contatti/${data.id}`, { replace: true });
      } else {
        const { data } = await gapi.put(`/contacts/${id}`, c);
        setC((p) => ({ ...p, ...data })); toast.success("Modifiche salvate");
      }
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.duplicati) setDups(d.duplicati); else toast.error(errMsg(e));
    } finally { setSaving(false); }
  };

  const act = async (path, ok) => { try { await gapi.post(path); toast.success(ok); load(); } catch (e) { toast.error(errMsg(e)); } };

  if (!c) return <p className="text-sm text-anthracite/50">Caricamento…</p>;
  const editable = isNew ? can("contatti", "creare") : can("contatti", "modificare");

  return (
    <div data-testid="contact-detail">
      <PageHeader eyebrow={<Link to="/gestionale/contatti">← Contatti</Link>} title={isNew ? "Nuovo contatto" : c.displayName}
        desc={!isNew ? `Creato il ${fmtDateTime(c.createdAt)} · ultimo contatto ${fmtDateTime(c.ultimoContatto)}${c.archiviato ? " · ARCHIVIATO" : ""}` : "Inserisci almeno un recapito: verranno controllati i possibili duplicati."}
        actions={<>
          {!isNew && can("contatti", "archiviare") && (c.archiviato
            ? <Btn variant="ghost" onClick={() => act(`/contacts/${id}/restore`, "Contatto ripristinato")} data-testid="contact-restore">Ripristina</Btn>
            : <Btn variant="ghost" onClick={() => act(`/contacts/${id}/archive`, "Contatto archiviato")} data-testid="contact-archive">Archivia</Btn>)}
          {editable && <Btn onClick={() => save()} disabled={saving} data-testid="contact-save">{saving ? "Salvataggio…" : "Salva"}</Btn>}
        </>} />
      {dups && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="contact-dups-warning">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Possibili duplicati</p>
          <ul className="mt-2">{dups.map((d) => <li key={d.id}><Link className="underline" to={`/gestionale/contatti/${d.id}`}>{d.displayName}</Link> — stesso {d.motivi.join(", ")}</li>)}</ul>
          <Btn variant="ghost" className="mt-3" onClick={() => save(true)} data-testid="contact-create-anyway">Crea comunque</Btn>
        </div>
      )}
      {!isNew && c.duplicati?.length > 0 && (
        <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm" data-testid="contact-dups-existing">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Possibili duplicati di questa scheda</p>
          {c.duplicati.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 mt-2">
              <span><Link className="underline" to={`/gestionale/contatti/${d.id}`}>{d.displayName}</Link> — stesso {d.motivi.join(", ")}</span>
              {can("contatti", "modificare") && <Btn variant="ghost" className="h-8 text-xs" onClick={() => setMergeDup(d)} data-testid="contact-merge-open">Anteprima unione</Btn>}
            </div>
          ))}
        </div>
      )}
      <div className="grid lg:grid-cols-3 gap-5">
        <Panel className="lg:col-span-2"><ContattoForm c={c} setC={setC} users={users} readOnly={!editable} /></Panel>
        <div className="space-y-5">
          {!isNew && editable && <Panel title="Registra un contatto"><RegistraContatto cid={id} onDone={load} /></Panel>}
          {!isNew && <Collegati c={c} />}
          {!isNew && <DocChecklist entita="contatto" entitaId={id} modello="cliente" />}
        </div>
        {!isNew && <Panel title="Cronologia" className="lg:col-span-3"><Timeline items={timeline} testid="contact-timeline" /></Panel>}
      </div>
      <MergeDialog keepId={id} dup={mergeDup} onClose={() => setMergeDup(null)} onDone={() => { setMergeDup(null); load(); }} />
    </div>
  );
}
